<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  getVacationPolicies,
  VacationPolicy,
  deleteVacationPolicy
} from '@/app/core/services/vacation-policy'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableVacationPolicies = ref(null)
const vacationPolicyForDelete = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('vacationPolicies.list.code'), table: 'code' },
  {
    name: 'is_default',
    title: t('vacationPolicies.list.default'),
    table: 'is_default'
  },
  {
    name: 'listEmployees',
    title: t('vacationPolicies.list.employees'),
    table: 'employees_name'
  },
  {
    name: 'listConcepts',
    title: t('vacationPolicies.list.effectiveConcepts'),
    table: 'concepts_name'
  },
  {
    name: 'period_vigency',
    title: t('vacationPolicies.list.validityMonths'),
    table: 'period_vigency'
  },
  {
    name: 'updated_at',
    title: t('vacationPolicies.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<VacationPolicy[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createVacationPolicy = () => {
  void router.push({ name: 'CreateVacationPolicy' })
}
const updateVacationPolicy = (id: any) => {
  void router.push({ name: 'UpdateVacationPolicy', params: { id } })
}
const getVacationPolicy = (id: any) => {
  void router.push({ name: 'GetVacationPolicy', params: { id } })
}

/* Eliminar política vacaciones */
const removeVacationPolicy = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('vacationPolicies.list.actionStatus', {
    vacationPolicy: values.code as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  showModal.value = true
  vacationPolicyForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteVacationPolicy(vacationPolicyForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('vacationPolicies.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void vacationPolicies()
    })
    .catch((error: any) => {
      notificationError(error, 'vacationPolicies', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar política de vacaciones */
const vacationPolicies = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getVacationPolicies(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'vacationPolicies', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('vacationPolicies.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('vacationPolicies.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="vacationPolicies" />
          <DBtn
            @click.prevent="vacationPolicies"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createVacationPolicy()"
          v-if="store.hasPermission('vacation_policie:create')">
          {{
            t('form.button.create', {
              form: t('vacationPolicies.list.title', 1)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableVacationPolicies"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), vacationPolicies()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), vacationPolicies()">
      <template #listEmployees="data: any">
        <div class="flex items-center">
          <div
            v-for="(employees, index) in data.employees_name
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ employees }}
          </div>
          <div
            v-if="data.employees_name.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.employees_name.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #listConcepts="data: any">
        <div class="flex items-center">
          <div
            v-for="(concept, index) in data.concepts_name
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ concept }}
          </div>
          <div
            v-if="data.concepts_name.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.concepts_name.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('vacation_policie:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getVacationPolicy(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('vacation_policie:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateVacationPolicy(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('vacation_policie:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeVacationPolicy(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
